/**
 * Session-replay input masking for PostHog / rrweb.
 *
 * WHY THIS EXISTS (read before editing):
 * PostHog's `session_recording` config sets
 *   maskInputOptions: { password: true, email: true, text: true }
 * intending to mask those inputs. But rrweb only applies its built-in
 * asterisking when NO `maskInputFn` is supplied. The moment a `maskInputFn`
 * exists, rrweb calls it for EVERY masked input and uses its return value
 * verbatim (see maskInputValue in posthog-js) — the per-type asterisking is
 * bypassed entirely. So a naive "mask only postal codes" function silently
 * un-masks passwords and emails, recording them in plaintext in replay.
 *
 * This function is therefore the single source of truth for the masking
 * policy: it masks auth fields and personal data, and passes everything else
 * through (so `maskAllInputs: false` still lets us see non-sensitive
 * interactions like grass type, lawn size, and search).
 */
const PERSONAL_AUTOCOMPLETE_TOKENS = new Set([
  'name',
  'given-name',
  'additional-name',
  'family-name',
  'nickname',
  'tel',
  'tel-country-code',
  'tel-national',
  'tel-area-code',
  'tel-local',
])

const PERSONAL_FIELD_NAMES = new Set([
  'name',
  'first-name',
  'firstname',
  'last-name',
  'lastname',
  'full-name',
  'fullname',
  'phone',
  'phone-number',
  'telephone',
  'message',
  'description',
  'subject',
])

export function maskReplayInput(text: string, element?: HTMLElement): string {
  const type = (element?.getAttribute?.('type') || '').toLowerCase()
  const autocomplete = (element?.getAttribute?.('autocomplete') || '').toLowerCase()
  const name = (element?.getAttribute?.('name') || '').toLowerCase()

  const isSensitive =
    // Passwords. Key off the autocomplete token too, not just type: a
    // show/hide-password toggle flips the input's `type` between 'password'
    // and 'text', but the autocomplete token stays put.
    type === 'password' ||
    autocomplete === 'current-password' ||
    autocomplete === 'new-password' ||
    // Emails (type="email", or a text input tagged as an email field).
    type === 'email' ||
    autocomplete === 'email' ||
    name === 'email' ||
    // Names, phone numbers, and free-form contact fields.
    type === 'tel' ||
    PERSONAL_AUTOCOMPLETE_TOKENS.has(autocomplete) ||
    PERSONAL_FIELD_NAMES.has(name) ||
    // Postal codes — the original selective-masking intent.
    autocomplete === 'postal-code' ||
    name === 'postal-code' ||
    // Street addresses. The lawn-size calculator deliberately keeps browser
    // autocomplete off so it does not fight Google Places, so `name` is the
    // durable privacy signal for that input.
    autocomplete === 'street-address' ||
    autocomplete === 'address-line1' ||
    autocomplete === 'address-line2' ||
    name === 'address' ||
    name === 'street-address'

  return isSensitive ? '*'.repeat(text.length) : text
}

/**
 * Remove precise-address params before PostHog stores pageview/referrer URLs.
 * Soil Temperature keeps its existing location scrub; `address` is removed on
 * every route so a copied or hand-authored lawn-size URL cannot leak a home
 * address into analytics.
 */
export function scrubSensitiveLocationParamsFromUrl(value: unknown): unknown {
  if (typeof value !== 'string') return value

  const hasAddress = /[?&]address=/.test(value)
  const hasSoilTemperatureLocation =
    value.includes('/tools/soil-temperature') && /[?&]location=/.test(value)

  if (!hasAddress && !hasSoilTemperatureLocation) return value

  try {
    const isAbsolute = /^[a-z][a-z\d+.-]*:\/\//i.test(value)
    const url = new URL(value, 'https://whatgrassisthis.invalid')
    url.searchParams.delete('address')
    if (url.pathname.startsWith('/tools/soil-temperature')) {
      url.searchParams.delete('location')
    }

    if (isAbsolute) return url.toString()
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    let scrubbed = value
      .replace(/([?&])address=[^&#]*/g, '$1')
      .replace(/[?&]$/, '')

    if (scrubbed.includes('/tools/soil-temperature')) {
      scrubbed = scrubbed
        .replace(/([?&])location=[^&#]*/g, '$1')
        .replace(/[?&]$/, '')
    }

    return scrubbed.replace('?&', '?')
  }
}
